import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';

interface Job {
  id: string;
  jobTitle: string;
  companyName: string;
  jobRole: string;
  eligibility: string;
  postedDate: any;
}

interface ApplicationForm {
  name: string;
  mobile: string;
  email: string;
  district: string;
  state: string;
  hasLaptop: string;
  resume: File | null;
}

const Careers: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [applicationForm, setApplicationForm] = useState<ApplicationForm>({
    name: '',
    mobile: '',
    email: '',
    district: '',
    state: '',
    hasLaptop: '',
    resume: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const jobsQuery = query(
        collection(db, 'jobs'),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(jobsQuery);
      
      const jobsData: Job[] = [];
      querySnapshot.forEach((doc) => {
        jobsData.push({
          id: doc.id,
          ...doc.data()
        } as Job);
      });

      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = (job: Job) => {
    setSelectedJob(job);
    setShowModal(true);
    setSubmitSuccess('');
    setSubmitError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setApplicationForm({ ...applicationForm, resume: e.target.files[0] });
    }
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      let resumeURL = '';

      // Upload resume to Firebase Storage
      if (applicationForm.resume) {
        const resumeRef = ref(storage, `resumes/${Date.now()}_${applicationForm.resume.name}`);
        await uploadBytes(resumeRef, applicationForm.resume);
        resumeURL = await getDownloadURL(resumeRef);
      }

      // Save application to Firestore
      await addDoc(collection(db, 'applications'), {
        name: applicationForm.name,
        email: applicationForm.email,
        phone: applicationForm.mobile,
        district: applicationForm.district,
        state: applicationForm.state,
        hasLaptop: applicationForm.hasLaptop,
        resume: resumeURL,
        jobTitle: selectedJob?.jobTitle || '',
        jobId: selectedJob?.id || '',
        companyName: selectedJob?.companyName || '',
        appliedDate: Timestamp.now(),
        status: 'pending'
      });

      setSubmitSuccess('Application submitted successfully!');
      setApplicationForm({
        name: '',
        mobile: '',
        email: '',
        district: '',
        state: '',
        hasLaptop: '',
        resume: null
      });

      setTimeout(() => {
        setShowModal(false);
        setSubmitSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitError('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <i className="fas fa-briefcase text-primary mr-3"></i>
            Career Opportunities
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Join our team and be part of something amazing. Explore current job openings and apply today!
          </p>
        </div>

        {/* Jobs Listing */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <i className="fas fa-spinner fa-spin text-5xl text-primary"></i>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20">
            <i className="fas fa-inbox text-8xl text-gray-300 mb-6"></i>
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Jobs Available</h3>
            <p className="text-gray-500">Check back later for new opportunities!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-primary w-12 h-12 rounded-full flex items-center justify-center">
                    <i className="fas fa-building text-white text-xl"></i>
                  </div>
                  <span className="text-xs text-gray-500">
                    {job.postedDate?.toDate().toLocaleDateString()}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{job.jobTitle}</h3>
                <p className="text-primary font-semibold mb-4">
                  <i className="fas fa-building mr-2"></i>
                  {job.companyName}
                </p>

                <div className="mb-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Job Role:</h4>
                  <p className="text-gray-600 text-sm line-clamp-3">{job.jobRole}</p>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-700 mb-2">Eligibility:</h4>
                  <p className="text-gray-600 text-sm line-clamp-2">{job.eligibility}</p>
                </div>

                <button
                  onClick={() => handleApplyClick(job)}
                  className="w-full bg-primary hover:bg-secondary text-white font-semibold py-3 px-6 rounded-md transition duration-300 flex items-center justify-center"
                >
                  <i className="fas fa-paper-plane mr-2"></i>
                  Apply Now
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Application Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-primary text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Apply for {selectedJob?.jobTitle}</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:text-gray-200 text-2xl"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <p className="text-gray-100 mt-2">{selectedJob?.companyName}</p>
            </div>

            <form onSubmit={handleSubmitApplication} className="p-6">
              {submitSuccess && (
                <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                  <i className="fas fa-check-circle mr-2"></i>
                  {submitSuccess}
                </div>
              )}

              {submitError && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {submitError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    placeholder="Enter your full name"
                    value={applicationForm.name}
                    onChange={(e) => setApplicationForm({ ...applicationForm, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    placeholder="10-digit mobile number"
                    value={applicationForm.mobile}
                    onChange={(e) => setApplicationForm({ ...applicationForm, mobile: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    placeholder="your.email@example.com"
                    value={applicationForm.email}
                    onChange={(e) => setApplicationForm({ ...applicationForm, email: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    District <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    placeholder="Enter your district"
                    value={applicationForm.district}
                    onChange={(e) => setApplicationForm({ ...applicationForm, district: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    placeholder="Enter your state"
                    value={applicationForm.state}
                    onChange={(e) => setApplicationForm({ ...applicationForm, state: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Do you have a laptop? <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={applicationForm.hasLaptop}
                    onChange={(e) => setApplicationForm({ ...applicationForm, hasLaptop: e.target.value })}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resume <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    required
                    accept=".pdf,.doc,.docx"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    onChange={handleFileChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-6 rounded-md transition duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-primary hover:bg-secondary text-white font-semibold py-3 px-6 rounded-md transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Submit Application
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Careers;